import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, DialogShell, Kbd } from '@timely/ui';

/**
 * Keyboard shortcuts for the places and the lists (DESIGN-SYSTEM §17).
 *
 *   /        jump to the list's search box
 *   g, then t / c / q / r / s   go to Today, Customers, Cheques, Reports, Settings
 *   Alt + ← / →   previous / next account (on an account)
 *   ?        this list
 *
 * They never fire while somebody is typing, or while a dialog is open over
 * the page, and every one of them has a button that does the same thing —
 * a shortcut is a faster way, never the only way.
 */
export interface ShortcutPlaces {
    today: string;
    customers: string;
    cheques: string | null;
    reports: string | null;
    settings: string | null;
}

const typing = (target: EventTarget | null): boolean => {
    const el = target as HTMLElement | null;
    return Boolean(el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)));
};

export function useShortcuts(places: ShortcutPlaces, onHelp: () => void) {
    const navigate = useNavigate();
    const pending = useRef<number | null>(null);
    const placesRef = useRef(places);
    placesRef.current = places;

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
            if (typing(e.target)) return;
            if (document.querySelector('[aria-modal="true"]')) return;

            if (pending.current !== null) {
                window.clearTimeout(pending.current);
                pending.current = null;
                const p = placesRef.current;
                const to = { t: p.today, c: p.customers, q: p.cheques, r: p.reports, s: p.settings }[e.key.toLowerCase()];
                if (to) { e.preventDefault(); navigate(to); }
                return;
            }

            if (e.key === '/') {
                const search = document.querySelector<HTMLInputElement>('[data-shortcut-search]');
                if (search) { e.preventDefault(); search.focus(); search.select(); }
                return;
            }
            if (e.key === '?') { e.preventDefault(); onHelp(); return; }
            if (e.key === 'g' && !e.shiftKey) {
                pending.current = window.setTimeout(() => { pending.current = null; }, 1200);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            if (pending.current !== null) window.clearTimeout(pending.current);
        };
    }, [navigate, onHelp]);
}

export function ShortcutsDialog({ places, onClose }: { places: ShortcutPlaces; onClose: () => void }) {
    const rows: [React.ReactNode, string][] = [
        [<Kbd key="k">/</Kbd>, 'Jump to the search box on Customers or Cheques'],
        [<><Kbd>g</Kbd> then <Kbd>t</Kbd></>, 'Go to Today'],
        [<><Kbd>g</Kbd> then <Kbd>c</Kbd></>, 'Go to Customers'],
        ...(places.cheques ? [[<><Kbd>g</Kbd> then <Kbd>q</Kbd></>, 'Go to Cheques'] as [React.ReactNode, string]] : []),
        ...(places.reports ? [[<><Kbd>g</Kbd> then <Kbd>r</Kbd></>, 'Go to Reports'] as [React.ReactNode, string]] : []),
        ...(places.settings ? [[<><Kbd>g</Kbd> then <Kbd>s</Kbd></>, 'Go to Settings'] as [React.ReactNode, string]] : []),
        [<><Kbd>Alt</Kbd> + <Kbd>←</Kbd> / <Kbd>→</Kbd></>, 'Previous or next account, on an account'],
        [<Kbd key="q">?</Kbd>, 'Show this list'],
        [<Kbd key="e">Esc</Kbd>, 'Close a dialog or a calendar'],
    ];
    return (
        <DialogShell
            title="Keyboard shortcuts"
            subtitle="None of them work while you are typing in a field."
            size="sm"
            onClose={onClose}
            footer={<Button type="button" variant="secondary" onClick={onClose} data-autofocus>Close</Button>}
        >
            {/* Focusable and named, so the list can be scrolled from the keyboard on a small screen. */}
            <dl tabIndex={0} aria-label="Keyboard shortcuts" className="divide-y divide-separator rounded-control">
                {rows.map(([keys, what], i) => (
                    <div key={i} className="flex items-center justify-between gap-4 py-2.5">
                        <dt className="text-body text-label-2">{what}</dt>
                        <dd className="flex items-center gap-1 text-caption text-label-3 flex-none">{keys}</dd>
                    </div>
                ))}
            </dl>
        </DialogShell>
    );
}

export function useShortcutHelp(): [boolean, () => void, () => void] {
    const [open, setOpen] = useState(false);
    const show = useCallback(() => setOpen(true), []);
    const hide = useCallback(() => setOpen(false), []);
    return [open, show, hide];
}
